import React from 'react';
import { PiGearSix } from "react-icons/pi";
import { TbDog } from "react-icons/tb";
import { FaCheckCircle, FaTimesCircle, FaWalking, FaBirthdayCake, FaNotesMedical, FaSyringe, FaExclamationTriangle } from "react-icons/fa";
import dogTeamsInfo from '../../../helpers/dogTeamInfoHelper';
import './DogCard.css';

function DogCard({ dog, onEdit, onManageVaccinations }) {
  const {
    name,
    breed,
    birthday,
    isMonthlyMember,
    isMonthlyMemberActive,
    totalVisits,
    sex,
    team,
    vaccinationStatus = {
      upToDate: [],
      missing: [],
      expired: [],
      unverified: []
    }
  } = dog;

  // Check if core vaccinations are up to date
  const hasValidRabies = vaccinationStatus.upToDate.some(vacc => 
    vacc.name === 'Rabies [Initial]' || vacc.name === 'Rabies [Continuing]'
  );

  const hasValidDAP = vaccinationStatus.upToDate.some(vacc =>
    vacc.name === 'DAP'
  );

  const vaccinesUpToDate = hasValidRabies && hasValidDAP;

  // Get unique core vaccinations with priority to up-to-date ones
  const getUniqueVaccinations = () => {
    const uniqueVaccines = new Map();

    // First add up-to-date vaccinations
    vaccinationStatus.upToDate.forEach(vacc => {
      if (['Rabies [Initial]', 'Rabies [Continuing]', 'DAP'].includes(vacc.name)) {
        const simpleName = vacc.name.startsWith('Rabies') ? 'Rabies' : vacc.name;
        if (!uniqueVaccines.has(simpleName)) {
          uniqueVaccines.set(simpleName, { ...vacc, displayName: simpleName });
        }
      }
    });

    // Then add unverified ones if not already present
    vaccinationStatus.unverified.forEach(vacc => {
      if (['Rabies [Initial]', 'Rabies [Continuing]', 'DAP'].includes(vacc.name)) {
        const simpleName = vacc.name.startsWith('Rabies') ? 'Rabies' : vacc.name;
        if (!uniqueVaccines.has(simpleName)) {
          uniqueVaccines.set(simpleName, { ...vacc, displayName: simpleName });
        }
      }
    });

    // Finally add expired ones if not already present
    vaccinationStatus.expired.forEach(vacc => {
      if (['Rabies [Initial]', 'Rabies [Continuing]', 'DAP'].includes(vacc.name)) {
        const simpleName = vacc.name.startsWith('Rabies') ? 'Rabies' : vacc.name;
        if (!uniqueVaccines.has(simpleName)) {
          uniqueVaccines.set(simpleName, { ...vacc, displayName: simpleName });
        }
      }
    });

    return Array.from(uniqueVaccines.values());
  };

  const uniqueCoreVaccinations = getUniqueVaccinations();

  // Check if there are any unverified core vaccinations
  const hasUnverifiedCore = vaccinationStatus.unverified.some(vacc =>
    ['Rabies [Initial]', 'Rabies [Continuing]', 'DAP'].includes(vacc.name)
  );

  const age = calculateAge(birthday);
  const birthdayIconColor = sex === 'male' ? 'teal' : 'pink';
  
  // Find the team info - support both old team names and new IDs/colors
  const teamInfo = dogTeamsInfo.find(t => 
    t.id === team || 
    t.color === team || 
    t.name === team ||
    // Legacy NFL team mapping (if any old data exists)
    (team === 'Cowboys' && t.color === 'Blue') ||
    (team === 'Eagles' && t.color === 'Green') ||
    (team === 'Giants' && t.color === 'Blue') ||
    (team === 'Commanders' && t.color === 'Red')
  ) || dogTeamsInfo[0]; // Default to first team if not found
  
  const teamLogoUrl = teamInfo.logo;
  const teamColor = getTeamColorHex(teamInfo.color);

  // Helper function to determine vaccination status
  const getVaccinationStatus = (vaccine) => {
    const isExpired = vaccinationStatus.expired.some(v => v._id === vaccine._id);
    const isUnverified = vaccinationStatus.unverified.some(v => v._id === vaccine._id);

    if (isExpired) {
      return { color: 'red', suffix: ' (expired)' };
    }
    if (isUnverified) {
      return { color: 'orange', suffix: ' (unverified)' };
    }
    return { color: 'green', suffix: '' };
  };

  return (
    <div className="dog-card" style={{ '--team-color': teamColor }}>
      <img src={teamLogoUrl} alt={`${teamInfo.name} logo`} className="dog-team-image"/>
      <div className="dog-name">{name}</div>
      <div className="dog-breed"><TbDog className="balloon-dog" style={{ color: 'green' }}/>{breed}</div>
      <div className="dog-age"><FaBirthdayCake className="cake" style={{ color: birthdayIconColor }}/> 
        {age.years > 0 
          ? `${age.years} ${age.years === 1 ? 'year' : 'years'} n' ${age.months > 0 ? `${age.months} ${age.months === 1 ? 'month' : 'months'}` : ''} old`
          : `${age.months} ${age.months === 1 ? 'month' : 'months'} old${age.months <= 9 ? ', just a puppy!' : ''}`
        }
      </div>
      <div className="dog-health">
        <FaNotesMedical className="records" style={{ color: vaccinesUpToDate ? 'green' : 'red' }}/><label>Records up to date:</label>
        {vaccinesUpToDate ? (
          <FaCheckCircle className="icon-green" />
        ) : hasUnverifiedCore ? (
          <FaExclamationTriangle className="icon-yellow" title="Some vaccinations need verification" />
        ) : (
          <FaTimesCircle className="icon-red" />
        )}
        <button className="manage-vaccinations-button" onClick={onManageVaccinations}>Manage Vaccinations</button>
      </div>
      {uniqueCoreVaccinations.length > 0 && (
        <div className="vaccination-list">
          <FaSyringe className="syringe" style={{ color: 'green' }}/> 
          Records:{" "}
          {uniqueCoreVaccinations.map((vaccine, index) => {
            const status = getVaccinationStatus(vaccine);
            return (
              <span key={vaccine._id} className="vaccine-item" style={{ color: status.color }}>
                {vaccine.displayName}
                {status.suffix}
                {index < uniqueCoreVaccinations.length - 1 ? ', ' : ''}
              </span>
            );
          })}
        </div>
      )}
      {isMonthlyMember && (
        <div className="dog-membership">
          <label>Current Monthly member:</label>
          {isMonthlyMemberActive ? <FaCheckCircle className="icon-green" /> : <FaTimesCircle className="icon-red" />}
        </div>
      )}
      <div className="dog-visits"><FaWalking className="walking"/> Times visited the park: {totalVisits}</div>
      <button className="edit-dog-button" onClick={() => onEdit(dog)}><PiGearSix className="edit-gear" />Edit Your Dog's Information</button>
    </div>
  );
}

export default DogCard;

function calculateAge(birthday) {
  const today = new Date();
  const birthDate = new Date(birthday);
  let years = today.getFullYear() - birthDate.getFullYear();
  let months = today.getMonth() - birthDate.getMonth();

  if (months < 0) {
    years--;
    months += 12;
  } else if (months === 0 && today.getDate() < birthDate.getDate()) {
    years--;
    months = 11;
  }

  return { years, months };
}

function getTeamColorHex(colorName) {
  const colorMap = {
    'Blue': '#0000FF',
    'Green': '#008000',
    'Orange': '#FFA500',
    'Purple': '#800080',
    'Red': '#FF0000'
  };
  return colorMap[colorName] || '#4CAF50'; // Default green if not found
}
